import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, TrendingDown, TrendingUp } from '../../../icons';
import './StatTile.css';

/**
 * StatTile — **요약 타일** (디자인 정본 D9 · D9-2).
 *
 * ── 2026-09-06 · D9-2 신설과 함께 `Card` 에서 **옮겨 나왔다** ──────────────────
 * 실측에서 이 물건이 **24파일 · 손짜기 9접두**로 흩어져 있었다(`hrpage__kpi` 80 ·
 * `hra__stat` 16 · `tmatt__stat` 11 · `tscope__stat` 7 · `asts__stat` 7 · `myatt__stat` 5 ·
 * `wc-master__kpi` 4). **숫자 크기가 넷 · 바탕이 둘**로 갈려 있었다.
 *
 * ★★ 여기 있던 것은 **증감을 이고 있는 큰 카드**(D9)였고 **프로덕션 사용이 0** 이었다.
 *    줄지어 서는 작은 요약(D9-2)을 따로 만들지 않고 **한 벌로 합친다** — 이름이 같은 물건이
 *    둘이면 다음 사람이 어느 것을 쓸지 매번 고른다.
 *
 * ★★★ **증감 색은 «의미»로 정한다. 방향으로 정하지 않는다.**
 *    매출이 오르면 좋은 일이지만 **이직률이 오르면 나쁜 일**이다.
 *
 * ★★★ **0 을 «없다»로 그리지 않는다.** 모르는 것 · 안 센 것 · 아직 시작 안 한 것은 전부 다르다.
 *    `value={null}` 은 **「—」(모른다)** 이고, 「아직 없음」처럼 **다른 사정**은 `emptyText` 로 적는다.
 *    0 을 주면 **0 이라고 적는다** — 그것이 사실일 때만 준다.
 *
 * ★★★ **「가는 타일」은 걸러지지 않고 «간다»** (2026-09-06 · 사용자 결정).
 *    `to` 를 주면 칸 전체가 `<Link>` 가 되고 **오른쪽 끝에 셰브론**이 선다. 필터 타일과 한 줄에
 *    섞여 서므로 **가르는 표시가 있어야 한다** — 근태 현황의 「퇴근 누락」이 그 자리다
 *    (어제 이전 누계라 오늘 표에서 거를 수가 없다 → 거르는 시늉 대신 이상 근태로 보낸다).
 *    ⚠ `to` 를 주는 타일은 **Router 안에서만** 그려진다(검사도 `MemoryRouter` 로 감싼다).
 */
export type StatTone = 'default' | 'primary' | 'warning' | 'danger';
export type StatSize = 'tile' | 'chip';

export interface StatTileProps {
  /** 무엇을 센 것인가. **색만으로 뜻을 만들지 않는다**(정본 P5) — 여기에 말이 있어야 한다. */
  label: ReactNode;
  /** 모르면 `null` — 0 으로 바꿔 그리지 않는다. */
  value: ReactNode | null;
  /** 숫자에 붙는 작은 단위 — 「명」·「일」·「원」. 라벨로 내리면 숫자가 무엇인지 늦게 안다. */
  unit?: ReactNode;
  /** 아래 한 줄 — 근거·기간·단서. 「지금의 판정」만 적는다(규칙은 도움말로 · 정본 P8). */
  note?: ReactNode;
  /** 증감. `tone` 이 색을 정하고 `dir` 은 화살표만 정한다. */
  delta?: { dir: 'up' | 'down'; label: ReactNode; tone?: 'good' | 'bad' | 'neutral' };
  /**
   * 급함 (2026-09-06 · D9-2). **왼쪽 띠와 숫자에만** 든다 —
   * 바탕은 안 물들인다(「골랐다」와 헷갈린다).
   */
  tone?: StatTone;
  /** `tile`(본문 격자 · 기본) · `chip`(화면 머리 · `PageHeader` actions 앞). */
  size?: StatSize;
  /**
   * 주면 **누를 수 있는 타일**이 된다 — 칸이 곧 필터다 (2026-09-06 · D9-2).
   * ★ 누르는 타일의 숫자는 **«몇 개 보이나»**를 적는다. 걸러도 안 바뀌는 전체 기준 숫자는
   *   거짓말이 된다(`Tabs` 의 건수와 같은 규칙).
   */
  onClick?: () => void;
  /**
   * 누르는 타일의 **이름** — 「출근만 보기」처럼 «무엇을 하는지»를 담는다 (2026-09-06 · D9-2).
   * 보이는 라벨(「출근」)만으로는 **눌러서 무엇이 되는지**를 못 말한다.
   * ⚠ 반드시 **보이는 라벨을 포함**해야 한다(WCAG 2.5.3 Label in Name) — 「출근」 → 「출근만 보기」.
   */
  ariaLabel?: string;
  /** 골랐나. `onClick` 이 있을 때만 뜻이 있다 — **바탕 틴트 + 테두리**로 그린다. */
  selected?: boolean;
  /**
   * 주면 **다른 화면으로 가는 타일**이 된다 — 칸 전체가 문이다 (2026-09-06 · D9-2).
   * ★ `onClick`(필터)과 **같이 주지 않는다.** 한 칸이 두 가지 일을 하면 누르는 사람이
   *   무엇이 일어날지 모른다. 「골랐다」도 없다 — 눌러서 화면이 바뀌면 남을 자리가 없다.
   * ★ 가는 타일의 숫자는 **전체 기준이어도 된다**(거르지 않으니 「몇 개 보이나」에 안 매인다).
   */
  to?: string;
  /** `null` 을 「—」 대신 이 말로 그린다 — 「아직 없음」·「안 셈」처럼 **사정이 다를 때**. */
  emptyText?: string;
  className?: string;
}

export function StatTile({
  label, value, unit, note, delta,
  tone = 'default', size = 'tile',
  onClick, selected = false, to, ariaLabel, emptyText,
  className,
}: StatTileProps) {
  const deltaTone = delta?.tone ?? 'neutral';
  const empty = value === null || value === undefined;
  const cls = [
    'wc-stat',
    `wc-stat--${size}`,
    tone !== 'default' ? `wc-stat--${tone}` : '',
    empty ? 'wc-stat--none' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  /*
   * ★★★ **칩은 «이름 먼저»가 DOM 순서여야 한다** (2026-09-06).
   *    종전에는 CSS `order` 로 뒤집었는데, 그러면 **눈에 보이는 순서와 읽어 주는 순서가 갈린다** —
   *    화면은 「승인 3시간 · 2명」인데 낭독기는 「2명 승인 3시간」으로 읽었다.
   *    쓰는 곳이 아직 없을 때 바로잡는다(옮길 화면이 0 이다).
   */
  /* ⚠ 이름을 `value` 로 두면 **props 의 `value` 를 가려** 제 자신을 참조한다(esbuild 가 막았다). */
  const valueEl = (
    <span className="wc-stat__value">
        {empty ? (
          /* 「—」는 «모른다»는 뜻이다. 0 과 다르다. */
          <span className="wc-stat__unknown" title={emptyText ? undefined : '아직 알 수 없습니다'}>
            {emptyText ?? '—'}
          </span>
        ) : (
          value
        )}
      {!empty && unit != null && <span className="wc-stat__unit">{unit}</span>}
    </span>
  );
  const labelEl = <span className="wc-stat__label">{label}</span>;

  const body = (
    <>
      {size === 'chip' ? <>{labelEl}{valueEl}</> : <>{valueEl}{labelEl}</>}
      {delta && (
        <span className={`wc-stat__delta is-${deltaTone}`}>
          {delta.dir === 'up' ? <TrendingUp aria-hidden /> : <TrendingDown aria-hidden />}
          {delta.label}
        </span>
      )}
      {note != null && <span className="wc-stat__note">{note}</span>}
      {/*
        ★ 기호는 **아이콘이 맡는다.** 라벨 안에 「→」를 «글자로» 적으면 낭독기가
          「오른쪽 화살표」라고 읽고, 라벨이 길면 화살표만 다음 줄로 떨어진다.
      */}
      {to != null && <ChevronRight className="wc-stat__go" aria-hidden />}
    </>
  );

  /*
    ★★★ `<button>` 의 UA 기본은 되돌려야 한다 — 세로 flex 카드를 단추로 만들면
    `align-items:center` 탓에 **왼쪽 띠가 폭 0** 이 되고 글자가 가운데로 몰린다
    (근태 현황에서 실제로 물렸다). 되돌리는 것은 CSS 가 한다(`button.wc-stat`).
    ★ `<button>` 안에 `<button>` 을 넣지 않는다 — 파서가 바깥을 먼저 닫는다.
  */
  if (onClick) {
    return (
      <button type="button" className={cls} aria-pressed={selected} aria-label={ariaLabel} onClick={onClick}>
        {body}
      </button>
    );
  }
  /* 가는 타일 — `aria-pressed` 를 달지 않는다(누르면 화면이 바뀌므로 「골라 둔 상태」가 없다). */
  if (to != null) {
    return <Link to={to} className={cls} aria-label={ariaLabel}>{body}</Link>;
  }
  return <div className={cls}>{body}</div>;
}

/** 타일이 서는 격자. 화면마다 다시 짜지 않는다. */
export function StatTiles({ children, ariaLabel, className }: {
  children: ReactNode; ariaLabel?: string; className?: string;
}) {
  return (
    <section className={['wc-stats', className ?? ''].filter(Boolean).join(' ')} aria-label={ariaLabel}>
      {children}
    </section>
  );
}
